'use client';

import type { ReactNode } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { channelLabel } from 'src/utils/platform/channel-label';

import { RADIUS } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';
import { PlatformIcon } from 'src/components/platform-icon/platform-icon';


/**
 * **채널 표 한 벌** — 「어디에 올라온 것인가」를 적는 모든 자리가 이것을 쓴다.
 *
 * 🔑 **아이콘이 먼저 눈에 걸리고 이름이 확인해 준다.** 이름만 적으면 「LIPS」·「Threads」·「X」가
 *   다 비슷한 회색 알약이라 훑을 때 안 읽힌다.
 * 🚨 **원값을 받는다**(`instagram`·`tiktok`·`x`) — 이름(`Instagram`)을 받으면 아이콘 파일을 못 찾는다.
 * ⚠️ **색을 입히지 않는다** — 인스타 그라데이션·유튜브 빨강이 사라지면 채널 식별이 안 된다
 *   (`PlatformIcon` 머리말).
 * ⚠️ **한 글자 이름은 안 적는다** — 「X」는 로고가 곧 이름이라 나란히 두면 「𝕏 X」로 두 번 읽힌다.
 * ⛔ 채널을 적는 자리에서 이 부품을 두고 글자만 적지 마라 — 화면마다 다른 표가 서면 같은 채널을
 *   다른 것으로 읽는다.
 */
export function ChannelTag({
  channel,
  size = 'sm',
  iconOnly = false,
}: {
  /** 채널 **원값** — 비면 아무것도 그리지 않는다 */
  channel: string;
  /** `sm` 은 줄 안에 끼는 표 · `md` 는 대상 카드의 머리줄 */
  size?: 'sm' | 'md';
  /** 이름을 감추고 아이콘만 — 자리가 좁은 격자 위 */
  iconOnly?: boolean;
}) {
  const id = String(channel ?? '').trim().toLowerCase();
  if (!id) return null;

  const label = channelLabel(id);
  const box = size === 'md' ? MD : SM;
  // 한 글자 이름은 로고가 곧 이름이다(머리말 ⚠️)
  const withLabel = !iconOnly && label.length > 1;

  return (
    <Tooltip title={label} disableInteractive>
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        aria-label={label}
        sx={{
          flexShrink: 0,
          height: box.h,
          px: withLabel ? box.px : box.pxIcon,
          borderRadius: RADIUS.pill,
          bgcolor: 'grey.100',
        }}
      >
        <ChannelGlyph channel={id} size={box.icon} />
        {withLabel && (
          <Typography noWrap sx={{ fontSize: box.font, fontWeight: 600, color: 'text.secondary' }}>
            {label}
          </Typography>
        )}
      </Stack>
    </Tooltip>
  );
}

/**
 * 채널 표와 **같은 알약**에 짧은 글을 적는다 — 줄 안에서 채널 표 옆에 나란히 서는 작은 수(「반응 19,681」).
 * 🔑 높이 · 모서리 · 바탕 · 글자 크기가 채널 표(`sm`)와 같다 — 한 줄에 섞여도 울퉁불퉁하지 않다.
 * 글자는 흐리지 않은 본문색이다(채널 이름보다 읽혀야 하는 값).
 */
export function ChannelSideTag({ children }: { children: ReactNode }) {
  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0, height: SM.h, px: SM.px, borderRadius: RADIUS.pill, bgcolor: 'grey.100', whiteSpace: 'nowrap', fontSize: SM.font, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}
    >
      {children}
    </Box>
  );
}

/**
 * 실물 위에 얹는 **아이콘만** — 섬네일 귀퉁이처럼 알약을 둘 자리가 없는 곳.
 * ⚠️ 이름은 도움말로만 나온다 — 그림 위에 글자를 얹으면 실물을 가린다.
 */
export function ChannelMarkIcon({ channel, size = 13 }: { channel: string; size?: number }) {
  const id = String(channel ?? '').trim().toLowerCase();
  if (!id) return null;
  return (
    <Tooltip title={channelLabel(id)} disableInteractive>
      <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
        <ChannelGlyph channel={id} size={size} />
      </Box>
    </Tooltip>
  );
}

// ---- 이하 비공개 ----------------------------------------------------------

/**
 * 그림 한 조각 — **자산(`PlatformIcon`)이 정본**이고, 자산이 없는 채널만 아이콘 세트로 떨어진다.
 *
 * 🚨 **표는 여기 하나다** — 화면이 제 아이콘 표를 따로 들면 같은 채널이 화면마다 다른 그림이 된다.
 */
function ChannelGlyph({ channel, size }: { channel: string; size: number }) {
  const fallback = ICONIFY_ONLY[channel];
  if (fallback) return <Iconify icon={fallback} width={size} sx={{ flexShrink: 0 }} />;
  return <PlatformIcon platformId={channel} size={size} />;
}

/**
 * `public/assets/icons/platforms/<채널>.svg` 가 **없는** 채널.
 * ⚠️ 자산이 생기면 여기서 빼라 — 그래야 색과 모양이 나머지와 같아진다.
 */
const ICONIFY_ONLY: Record<string, string> = {
  meta: 'socials:facebook',
  facebook: 'socials:facebook',
};

/** 알약 높이는 고정이다 — 글자 크기에 맡기면 줄이 울퉁불퉁해진다 */
const SM = { h: 19, icon: 11, font: 10.5, px: 0.625, pxIcon: 0.375 };
const MD = { h: 22, icon: 13, font: 11.5, px: 1, pxIcon: 0.5 };
